import { motion } from "framer-motion";
import { FaFolderOpen } from "react-icons/fa";
import ImportCard from "./ImportCard";
import { useCallback, useEffect, useRef, useState } from "react";
import GreenmileService from "../../../services/resource/GreenmileService";
import { toast } from "react-toastify";
import SapServiceRetornoEntrega from "../../../services/resource/SapServiceRetornoEntrega";
import SapServiceEntregaVolume from "../../../services/resource/SapServiceEntregaVolume";
import RomaneioService from "../../../services/resource/romaneioService";
import { GerarComissoes } from "../GerarComissoes";
import { ANOS, MESES } from "../../../utils/options";
import { Dialog } from "primereact/dialog";
import SapEntregaVolumePage from "../../volume-entregue/SapEntregaVolumePage";
import SapRetornoPage from "../../volume-entregue/SapRetornoPage";
import RotaGreenmilePage from "../../rotas-finalizadas/RotaGreenmilePage";
import RomaneioPage from "./RomaneioPage";

function ImportacaoMain() {
    const greenmileService = new GreenmileService();
    const sapEntrega = new SapServiceEntregaVolume();
    const sapRetorno = new SapServiceRetornoEntrega();
    const romaneioService = new RomaneioService();

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial); const [resultadoGreenmile, setResultadoGreenmile] = useState(null);
    const [resultadoEntregaSap, setResultadoEntregaSap] = useState(null);
    const [resultadoRetornoSap, setResultadoRetornoSap] = useState(null);
    const [resultadoImportacaoDA, setResultadoImportacaoDA] = useState(null);
    const [showGerenciamento, setShowGerenciamento] = useState({ visible: false, type: null });
    const [loadingType, setLoadingType] = useState(null);
    const [sortKey, setSortKey] = useState("dl");
    const [statusImportacao, setStatusImportacao] = useState({});

    // evita atualizar a tela (e continuar consultando o servidor) depois que ela foi fechada
    const montadoRef = useRef(true);
    useEffect(() => {
        montadoRef.current = true;
        return () => { montadoRef.current = false; };
    }, []);

    // Andamento de cada importação, exibido no próprio card (substitui o toast de loading)
    const iniciarStatus = (type, file) => {
        const agora = Date.now();
        setStatusImportacao(s => ({
            ...s,
            [type]: {
                arquivo: file.name,
                tamanho: file.size,
                status: "running",
                etapa: "Enviando arquivo...",
                inicio: agora,
                fim: null,
                erro: null,
                logs: [{ hora: agora, tipo: "info", msg: `Arquivo selecionado: ${file.name}` }, { hora: agora, tipo: "info", msg: "Enviando arquivo..." }],
            },
        }));
    };

    const atualizarEtapa = (type, etapa) => {
        setStatusImportacao(s => ({
            ...s,
            [type]: { ...s[type], etapa, logs: [...s[type].logs, { hora: Date.now(), tipo: "info", msg: etapa }] },
        }));
    };

    // Atualiza o andamento em tempo real (percentual, contagem, estimativa); só vira linha de log quando a etapa muda
    const atualizarProgresso = (type, { etapa, percentual, atual, total, restante, detalhe }) => {
        setStatusImportacao(s => {
            const anterior = s[type];
            if (!anterior) return s;
            const mudouEtapa = etapa && etapa !== anterior.etapa;
            return {
                ...s,
                [type]: {
                    ...anterior,
                    etapa: etapa ?? anterior.etapa,
                    percentual,
                    atual,
                    total,
                    restante,
                    detalhe,
                    logs: mudouEtapa ? [...anterior.logs, { hora: Date.now(), tipo: "info", msg: etapa }] : anterior.logs,
                },
            };
        });
    };

    const finalizarStatus = (type, erro = null) => {
        const agora = Date.now();
        setStatusImportacao(s => ({
            ...s,
            [type]: {
                ...s[type],
                status: erro ? "error" : "success",
                percentual: erro ? s[type].percentual : (s[type].percentual != null ? 100 : undefined),
                restante: null,
                detalhe: null,
                etapa: erro ? "Falha na importação" : "Importação concluída",
                fim: agora,
                erro,
                logs: [
                    ...s[type].logs,
                    erro
                        ? { hora: agora, tipo: "error", msg: erro.mensagem }
                        : { hora: agora, tipo: "success", msg: "Importação concluída com sucesso" },
                ],
            },
        }));
    };

    const handleImport = useCallback((type, file) => {

        const formData = new FormData();
        formData.append("file", file);

        iniciarStatus(type, file);

        // SAP Retorno roda em 2 etapas separadas (remove -> importa), pra dar pra mostrar o
        // andamento real de cada uma - as demais importações continuam numa chamada só. O vínculo
        // com a escala de rota (Carga de Entrega) acontece à parte, em "Gerar Comissões".
        if (type === "sap-retorno") {
            handleImportRetornoSap(formData);
            return;
        }

        // Pedidos DA roda em segundo plano no servidor; a tela acompanha o andamento por consulta a cada 1s
        if (type === "da-pedidos") {
            handleImportDA(formData);
            return;
        }

        let service;
        let setResultado;
        let nomeImportacao;

        if (type === "greenmile") {
            service = greenmileService;
            setResultado = setResultadoGreenmile;
            nomeImportacao = "GreenMile";
            setResultadoGreenmile(null);
        }

        if (type === "sap-entregas") {
            service = sapEntrega;
            setResultado = setResultadoEntregaSap;
            nomeImportacao = "SAP Entregas";
            setResultadoEntregaSap(null);
        }

        setLoadingType(type); // 🔒 trava os cards

        atualizarEtapa(type, `Importando arquivo do ${nomeImportacao}...`);

        service.importar(formData)
            .then(res => {
                setResultado(res.data);
                finalizarStatus(type);
                toast.success(`Arquivo do ${nomeImportacao} importado com sucesso!`, { autoClose: 3000 });
            })
            .catch((e) => {
                console.error(e);
                finalizarStatus(type, extrairErro(e));
            })
            .finally(() => {
                setLoadingType(null); // 🔓 libera os cards
            });

    }, []);

    const handleImportDA = useCallback(async (formData) => {
        setResultadoImportacaoDA(null);
        setLoadingType("da-pedidos"); // 🔒 trava os cards
        atualizarEtapa("da-pedidos", "Enviando arquivo...");

        const INTERVALO_MS = 1000;
        const MAX_FALHAS_SEGUIDAS = 8;

        try {
            const inicio = await romaneioService.importarEmSegundoPlano(formData);
            const processoId = inicio.data.processoId;
            let falhas = 0;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (!montadoRef.current) return;
                await new Promise(r => setTimeout(r, INTERVALO_MS));
                if (!montadoRef.current) return;

                let p;
                try {
                    p = (await romaneioService.consultarImportacao(processoId)).data;
                    falhas = 0;
                } catch (e) {
                    // falha de rede passageira não derruba o acompanhamento; a importação segue no servidor
                    if (++falhas >= MAX_FALHAS_SEGUIDAS) throw e;
                    continue;
                }

                if (p.status === "ERRO") {
                    throw Object.assign(new Error(p.mensagemErro), { response: { data: { message: p.mensagemErro } } });
                }

                const contagem = p.total > 0 ? ` (${p.atual.toLocaleString("pt-BR")} de ${p.total.toLocaleString("pt-BR")})` : "";
                atualizarProgresso("da-pedidos", {
                    etapa: p.etapaDescricao,
                    percentual: p.percentual,
                    atual: p.atual,
                    total: p.total,
                    restante: p.segundosRestantes,
                    detalhe: contagem,
                });

                if (p.status === "CONCLUIDO") {
                    setResultadoImportacaoDA(p.resultado);
                    finalizarStatus("da-pedidos");
                    toast.success("Arquivo do Pedidos - DA importado com sucesso!", { autoClose: 3000 });
                    return;
                }
            }
        } catch (e) {
            console.error(e);
            finalizarStatus("da-pedidos", extrairErro(e));
        } finally {
            setLoadingType(null); // 🔓 libera os cards
        }
    }, []);

    const handleImportRetornoSap = useCallback((formData) => {
        setResultadoRetornoSap(null);
        setLoadingType("sap-retorno"); // 🔒 trava os cards

        atualizarEtapa("sap-retorno", "Removendo arquivos...");

        sapRetorno.prepararImportacao(formData)
            .then(res => {
                const processoId = res.data.processoId;
                atualizarEtapa("sap-retorno", "Importando dados...");
                return sapRetorno.importarDados(processoId);
            })
            .then(resDados => {
                setResultadoRetornoSap(resDados.data);
                finalizarStatus("sap-retorno");
                toast.success("Arquivo do SAP Retorno importado com sucesso!", { autoClose: 3000 });
            })
            .catch((e) => {
                console.error(e);
                finalizarStatus("sap-retorno", extrairErro(e));
            })
            .finally(() => {
                setLoadingType(null); // 🔓 libera os cards
            });
    }, []);

    const abrirGerenciamento = (type) => {
        setShowGerenciamento({ visible: true, type: type })
    }

    const fecharGerenciamento = () => {
        setShowGerenciamento({ visible: false, type: null })
    }
    return (
        <div className="min-h-screen bg-background">

            <header className="px-4 py-3 border-b border-border bg-card/80 backdrop-blur-sm flex justify-between">
                <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary p-2">
                        <FaFolderOpen className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-foreground">
                            Central de Importações
                        </h1>
                        <p className="text-xs text-muted-foreground">
                            Central das importações de arquivos para comissões de motoristas
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {[
                        { key: "dl", label: "Dist. Logístico" },
                        { key: "da", label: "Dist. Autorizado" },
                        { key: "geracao-comissao", label: "Gerar Comissões" },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setSortKey(opt.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 text-nowrap
                                ${sortKey === opt.key
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-card text-muted-foreground border-border hover:border-teal-300"}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="container px-4 py-8">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                    {sortKey === "dl" &&
                        <ImportCard
                            type="greenmile"
                            accept=".csv"
                            onImport={handleImport}
                            service={greenmileService}
                            responseResult={resultadoGreenmile}
                            loading={loadingType !== null}
                            isLoading={loadingType === "greenmile"}
                            status={statusImportacao["greenmile"]}
                            showGerenciamento
                            abrirGerenciamento={abrirGerenciamento}
                        />
                    }

                    {sortKey === "dl" &&
                        <ImportCard
                            type="sap-entregas"
                             accept=".csv"
                            onImport={handleImport}
                            service={sapEntrega}
                            responseResult={resultadoEntregaSap}
                            loading={loadingType !== null}
                            isLoading={loadingType === "sap-entregas"}
                            status={statusImportacao["sap-entregas"]}
                            showGerenciamento
                            abrirGerenciamento={abrirGerenciamento}
                        />
                    }

                    {sortKey === "dl" &&
                        <ImportCard
                            type="sap-retorno"
                             accept=".csv"
                            onImport={handleImport}
                            service={sapRetorno}
                            responseResult={resultadoRetornoSap}
                            loading={loadingType !== null}
                            isLoading={loadingType === "sap-retorno"}
                            status={statusImportacao["sap-retorno"]}
                            showGerenciamento
                            abrirGerenciamento={abrirGerenciamento}
                        />
                    }

                    {sortKey === "da" &&
                        <ImportCard
                            type="da-pedidos"
                             accept=".xlsx, .xls"
                            onImport={handleImport}
                            service={romaneioService}
                            responseResult={resultadoImportacaoDA}
                            loading={loadingType !== null}
                            isLoading={loadingType === "da-pedidos"}
                            status={statusImportacao["da-pedidos"]}
                            showGerenciamento
                            abrirGerenciamento={abrirGerenciamento}
                        />
                    }

                    {sortKey === "geracao-comissao" &&
                        <div className="md:col-span-3">
                            <div className="grid px-4">
                                <label>Selecione a Data para geração de comissão: </label>
                                <div className="flex">
                                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">
                                        <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                                        <select
                                            value={mes}
                                            onChange={e => setMes(e.target.value)}
                                            className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                                        >
                                            {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                        </select>
                                        <span className="text-muted-foreground">|</span>
                                        <select
                                            value={ano}
                                            onChange={e => setAno(Number(e.target.value))}
                                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                                        >
                                            {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}

                                        </select>
                                    </div>
                                </div>
                            </div>
                            <GerarComissoes ano={ano} mes={mes} />
                        </div>
                    }

                </motion.div>

                <Dialog
                    visible={showGerenciamento.visible}
                    onHide={fecharGerenciamento}
                    className="w-[98%]"
                    header={
                        <div>
                            {showGerenciamento.type === "sap-entregas" && <Header title="Entregas de volume - ZSD0092" subtitle="Consulte e ajuste os registros de entrega do relatório SAP." />}
                            {showGerenciamento.type === "sap-retorno" && <Header title="Retornos de Entregas - ZS0090" subtitle="Consulte e ajuste os registros de retorno do relatório SAP ." />}
                            {showGerenciamento.type === "greenmile" && <Header title="Rotas Finalizadas - Greenmile" subtitle="Consulte e ajuste os registros de rota finalizada do relatório Greenmile." />}
                            {showGerenciamento.type === "da-pedidos" && <Header title="Romaneios - Pedidos DA" subtitle="Consulte e ajuste os romaneios e os pedidos vinculados a eles." />}
                        </div>
                    }
                    blockScroll={true}
                >
                    {showGerenciamento.type === "sap-entregas" && <SapEntregaVolumePage />}
                    {showGerenciamento.type === "sap-retorno" && <SapRetornoPage />}
                    {showGerenciamento.type === "greenmile" && <RotaGreenmilePage />}
                    {showGerenciamento.type === "da-pedidos" && <RomaneioPage />}
                </Dialog>
            </main>
        </div>
    );

}

export default ImportacaoMain;

// Monta a mensagem principal e um log detalhado a partir do erro do axios
function extrairErro(e) {
    const response = e?.response;
    const data = response?.data;

    const mensagem =
        (typeof data === "string" && data.trim()) ||
        data?.message ||
        data?.error ||
        data?.detail ||
        e?.message ||
        "Erro desconhecido ao importar o arquivo.";

    const detalhes = [
        response?.status && `Status: HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`,
        e?.config?.url && `Requisição: ${e.config.method?.toUpperCase() ?? ""} ${e.config.url}`,
        e?.code && `Código: ${e.code}`,
        data && typeof data === "object" && `Resposta:\n${JSON.stringify(data, null, 2)}`,
    ].filter(Boolean).join("\n");

    return { mensagem, detalhes };
}

function Header({ title, subtitle }) {
    return (
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <div className="mb-1 flex items-center gap-2 text-primary">
                    <i className="fa fa-truck text-sm" />
                    <span className="text-xs font-semibold uppercase tracking-wide">Integração SAP</span>
                </div>
                <h1 className="text-xl font-bold font-display text-foreground">{title}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            </div>
        </div>
    )
}